/**
 * 통합 알림함(Activity) 서비스 (Phase 2)
 * 서버 계약: server/routes/activity.js — GET /api/activity?tab=, GET /unread-count({count, tabs}),
 * POST /:id/read, POST /read-all?tab=. 실시간 갱신은 socketService의
 * activity:new / activity:read CustomEvent를 컴포넌트가 직접 구독한다.
 *
 * 탭 키: 'all' | 'people'(멘션·답글·리액션) | 출처 키('approval' …). 탭 목록은 서버가 정한다.
 */
import { apiFetch } from './apiClient';
import { ActivityItem, ActivityTab } from '../types';

export interface ActivityListResult {
  items: ActivityItem[];
  hasMore: boolean;
}

export interface ActivityUnreadResult {
  count: number;
  tabs: ActivityTab[];
}

/** 이 알림이 속한 탭 키 — 'all'에는 모두 속하므로 그 외 하나를 돌려준다 */
export function activityTabOf(item: Pick<ActivityItem, 'source'>): string {
  return item.source ? item.source : 'people';
}

export const activityService = {
  /** 알림 목록 커서 페이징 (id DESC). before 미지정 시 최신부터 */
  async getActivity(tab = 'all', before?: number, limit = 30): Promise<ActivityListResult> {
    const params = new URLSearchParams();
    if (tab && tab !== 'all') params.set('tab', tab);
    if (before) params.set('before', String(before));
    params.set('limit', String(limit));
    return apiFetch<ActivityListResult>(`/activity?${params.toString()}`);
  },

  /** 미읽음 수 (로그인/새로고침 시 뱃지 초기화) */
  async getUnreadCount(): Promise<number> {
    const res = await apiFetch<{ count: number }>('/activity/unread-count');
    return res.count || 0;
  },

  /** 미읽음 수 + 탭 목록 (알림함 패널용). 구버전 서버는 tabs가 없다 → 빈 배열 */
  async getUnreadSummary(): Promise<ActivityUnreadResult> {
    const res = await apiFetch<{ count: number; tabs?: ActivityTab[] }>('/activity/unread-count');
    return { count: res.count || 0, tabs: Array.isArray(res.tabs) ? res.tabs : [] };
  },

  /** 단건 읽음 처리 (fire-and-forget 사용 권장 — 실패해도 UI 흐름 유지) */
  async markRead(id: number): Promise<void> {
    await apiFetch(`/activity/${id}/read`, { method: 'POST' });
  },

  /** 탭 단위 일괄 읽음 처리 ('all' = 전체) */
  async markAllRead(tab = 'all'): Promise<void> {
    const q = tab && tab !== 'all' ? `?tab=${encodeURIComponent(tab)}` : '';
    await apiFetch(`/activity/read-all${q}`, { method: 'POST' });
  },
};
